// src/components/devices/deviceForms/ZigbeeDeviceForm.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';

const ZigbeeDeviceForm = ({ onSubmit, onCancel, isAdding }) => {
  const { apiRequest } = useAuth();
  const [formData, setFormData] = useState({
    discoveryTimeout: 600, // seconds (10 minutes)
  });
  const [discoveryStatus, setDiscoveryStatus] = useState({
    isConnected: false,
    permitJoin: false,
    timeRemaining: null
  });
  const [statusError, setStatusError] = useState(null);

  // Fetch current discovery status
  useEffect(() => {
    fetchDiscoveryStatus();
  }, []);

  const fetchDiscoveryStatus = async () => {
    try {
      const { response, data } = await apiRequest('/api/devices/zigbee/discovery/status');
      if (response.ok) {
        setDiscoveryStatus(data.data);
        setStatusError(null);
      } else {
        setStatusError('Unable to get Zigbee status');
      }
    } catch (error) {
      console.error('Error fetching discovery status:', error);
      setStatusError('Unable to connect to Zigbee network');
    }
  };
  
  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Submit the form
    onSubmit(formData);
  };
  
  return (
    <form className="device-form" onSubmit={handleSubmit}>
      <div className="discovery-info">
        <h4>Zigbee Device Pairing</h4>
        <p>
          To add a new Zigbee device, you need to put it in pairing mode first. 
          The system will enable discovery mode and automatically detect new devices.
        </p>
        
        <h4>How to add a new Zigbee device:</h4>
        <ul>
          <li>Make sure your Zigbee device is powered on</li>
          <li>Put your device in pairing/reset mode (check your device manual for instructions)</li>
          <li>Start discovery mode by clicking the button below</li>
          <li>Wait for the device to be discovered (usually takes 30-60 seconds)</li>
          <li>Once discovered, the device will automatically appear in your device list</li>
        </ul>
      </div>
      
      <div className="status-section">
        <h4>Zigbee Network Status</h4>
        <div className="status-row">
          <span className="status-label">Connection:</span>
          <span className={`status-value ${discoveryStatus.isConnected ? 'connected' : 'disconnected'}`}>
            {discoveryStatus.isConnected ? 'Connected' : 'Disconnected'}
          </span>
        </div>
        <div className="status-row">
          <span className="status-label">Discovery Mode:</span>
          <span className={`status-value ${discoveryStatus.permitJoin ? 'enabled' : 'disabled'}`}>
            {discoveryStatus.permitJoin ? 'Enabled' : 'Disabled'}
          </span>
        </div>
        {discoveryStatus.permitJoin && discoveryStatus.timeRemaining && (
          <div className="status-row">
            <span className="status-label">Time Remaining:</span>
            <span className="status-value">
              {Math.floor(discoveryStatus.timeRemaining / 60)}:{(discoveryStatus.timeRemaining % 60).toString().padStart(2, '0')}
            </span>
          </div>
        )}
        {statusError && (
          <div className="status-error">{statusError}</div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="discoveryTimeout">Discovery Timeout</label>
        <select
          id="discoveryTimeout"
          name="discoveryTimeout"
          value={formData.discoveryTimeout}
          onChange={handleChange}
          disabled={discoveryStatus.permitJoin}
        >
          <option value={60}>1 minutes</option>
          <option value={120}>2 minutes</option>
          <option value={240}>3 minutes</option>
          
        </select>
        <div className="input-description">
          How long to keep discovery mode active
        </div>
      </div>
      
      <div className="form-actions">
        <button 
          type="button" 
          className="cancel-button" 
          onClick={onCancel}
          disabled={isAdding}
        >
          Cancel
        </button>
        <button 
          type="submit" 
          className="submit-button"
          disabled={isAdding || !discoveryStatus.isConnected || discoveryStatus.permitJoin}
        >
          {isAdding ? 'Enabling Discovery...' : 
           discoveryStatus.permitJoin ? 'Discovery Already Active' :
           !discoveryStatus.isConnected ? 'Zigbee Not Connected' :
           'Start Discovery Mode'}
        </button>
      </div>
    </form>
  );
};

export default ZigbeeDeviceForm;